import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../lib/api';
import { STICKY_COLORS, stickyColorFor, clientColorFor, clientColorAuto } from '../lib/stickyColors';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash2, StickyNote, AtSign, X, Check, Calendar, LayoutGrid, List as ListIcon, Archive, ArchiveRestore, Link as LinkIcon, Pin } from 'lucide-react';
import InitialsAvatar from '../components/InitialsAvatar';

const COLORS = STICKY_COLORS;
const colorFor = stickyColorFor;

// Categories with a signature sticky colour each
const CATEGORIES = [
  { id: 'client',   label: 'client',   color: 'lilac' },
  { id: 'talent',   label: 'talent',   color: 'pink' },
  { id: 'personal', label: 'personal', color: 'sky' },
  { id: 'to_film',  label: 'to film',  color: 'mint' },
];
const categoryFor = (id) => CATEGORIES.find((c) => c.id === id);

// -----------------------------------------------------------------------------
// Date helpers
// -----------------------------------------------------------------------------
const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const daysUntil = (iso) => {
  if (!iso) return null;
  const d = startOfDay(new Date(iso));
  const now = startOfDay(new Date());
  return Math.round((d.getTime() - now.getTime()) / 86400000);
};
const fmtDueShort = (iso) => {
  if (!iso) return '';
  const dd = daysUntil(iso);
  if (dd === 0) return 'today';
  if (dd === 1) return 'tomorrow';
  if (dd === -1) return 'yesterday';
  if (dd !== null && dd < 0) return `${Math.abs(dd)}d overdue`;
  if (dd !== null && dd <= 7) return `in ${dd}d`;
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch { return iso; }
};
const fmtDueLongHeading = (iso) => {
  if (!iso) return 'no due date';
  const dd = daysUntil(iso);
  if (dd === 0) return 'today';
  if (dd === 1) return 'tomorrow';
  if (dd === -1) return 'yesterday';
  if (dd !== null && dd < 0) return `overdue · ${Math.abs(dd)}d`;
  try {
    return new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return iso; }
};

// -----------------------------------------------------------------------------
// Inline due-date chip (bottom-left of sticky / left of a list row)
// -----------------------------------------------------------------------------
const DueDateChip = ({ iso, editable, onChange, small = false }) => {
  const [editing, setEditing] = useState(false);
  const dd = daysUntil(iso);
  const overdue = dd !== null && dd < 0;
  const today = dd === 0;
  const cls = overdue
    ? 'bg-red-50 text-red-700 border-red-200'
    : today
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : iso
        ? 'bg-white/70 text-slate-600 border-white/70'
        : 'bg-white/40 text-slate-400 border-dashed border-slate-300';

  if (editing) {
    return (
      <input
        type="date"
        autoFocus
        defaultValue={iso ? String(iso).slice(0, 10) : ''}
        onBlur={(e) => { setEditing(false); onChange(e.target.value || ''); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { setEditing(false); onChange(e.target.value || ''); } if (e.key === 'Escape') setEditing(false); }}
        onClick={(e) => e.stopPropagation()}
        className={`h-6 text-[11px] px-1.5 rounded border border-pink-300 bg-white outline-none focus:ring-2 focus:ring-pink-200 ${small ? '' : ''}`}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); if (editable) setEditing(true); }}
      disabled={!editable}
      title={iso ? new Date(iso).toDateString() : 'add a due date'}
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] border ${cls} ${editable ? 'hover:brightness-95 cursor-pointer' : 'cursor-default'}`}
      data-testid="todo-due-chip"
    >
      <Calendar className="w-3 h-3" />
      {iso ? fmtDueShort(iso) : 'add date'}
      {iso && editable && (
        <span
          role="button"
          onClick={(e) => { e.stopPropagation(); onChange(''); }}
          className="ml-0.5 text-slate-400 hover:text-red-600"
        >
          <X className="w-2.5 h-2.5" />
        </span>
      )}
    </button>
  );
};

// -----------------------------------------------------------------------------
// Inline link chip — small "add link" box, becomes a clickable link once set
// -----------------------------------------------------------------------------
const LinkChip = ({ url, editable, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(url || '');
  useEffect(() => { setDraft(url || ''); }, [url]);

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { setEditing(false); onChange(draft.trim()); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { setEditing(false); onChange(draft.trim()); }
          if (e.key === 'Escape') { setEditing(false); setDraft(url || ''); }
        }}
        onClick={(e) => e.stopPropagation()}
        placeholder="paste a link…"
        className="h-6 text-[11px] px-1.5 rounded border border-pink-300 bg-white outline-none focus:ring-2 focus:ring-pink-200 w-36"
        data-testid="todo-link-input"
      />
    );
  }

  if (url) {
    const href = url.startsWith('http') ? url : `https://${url}`;
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-white/70 backdrop-blur px-1.5 py-0.5 border border-white/70 text-[10px]">
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1 text-pink-700 hover:text-pink-900"
          data-testid="todo-link-open"
        >
          <LinkIcon className="w-3 h-3 shrink-0" />
          <span className="truncate max-w-[110px]">{url.replace(/^https?:\/\//, '')}</span>
        </a>
        {editable && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
            className="text-slate-400 hover:text-red-600"
            data-testid="todo-link-remove"
          ><X className="w-2.5 h-2.5" /></button>
        )}
      </span>
    );
  }

  return editable ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      title="add a link"
      className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/40 border border-dashed border-slate-300 text-slate-400 hover:text-pink-600 hover:border-pink-300 shrink-0"
      data-testid="todo-link-add"
    >
      <LinkIcon className="w-3 h-3" />
    </button>
  ) : null;
};

// -----------------------------------------------------------------------------
// Team tag chip + picker (shared)
// -----------------------------------------------------------------------------
const TagChip = ({ user, onRemove, small = false }) => (
  <span className={`inline-flex items-center gap-1 rounded-full bg-white/60 backdrop-blur px-1.5 ${small ? 'py-0' : 'py-0.5'} border border-white/70`} title={user?.name}>
    <InitialsAvatar name={user?.name || '?'} size={small ? 14 : 16} className="text-[8px]" />
    <span className="text-[10px] font-medium text-slate-700 truncate max-w-[80px]">{user?.name || '—'}</span>
    {onRemove && (
      <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="text-slate-400 hover:text-red-600">
        <X className="w-3 h-3" />
      </button>
    )}
  </span>
);

const TagPicker = ({ directory, currentTags, onAdd, ownerLike }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [anchor, setAnchor] = useState(null);
  const btnRef = React.useRef(null);
  const options = useMemo(() => {
    const s = q.trim().toLowerCase();
    return directory
      .filter((u) => u.user_id !== ownerLike)
      .filter((u) => !currentTags.includes(u.user_id))
      .filter((u) => !s || (u.name || '').toLowerCase().includes(s));
  }, [directory, currentTags, ownerLike, q]);

  const openPicker = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setAnchor({ top: r.bottom + 4, left: r.left });
    }
    setOpen((o) => !o);
  };

  return (
    <span className="inline-block">
      <button
        ref={btnRef}
        onClick={openPicker}
        className="inline-flex items-center gap-1 rounded-full bg-white/60 backdrop-blur px-1.5 py-0.5 border border-white/70 text-[10px] text-slate-600 hover:bg-white/80"
        title="tag a team-mate"
        data-testid="todo-tag-picker"
      >
        <AtSign className="w-3 h-3" /> tag
      </button>
      {open && anchor && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[9999] w-56 bg-white rounded-md shadow-lg border border-slate-200 p-2"
            style={{ top: anchor.top, left: anchor.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="search team…"
              className="h-7 w-full text-xs border border-slate-200 rounded px-2 outline-none focus:ring-2 focus:ring-pink-200"
            />
            <div className="mt-1 max-h-48 overflow-y-auto">
              {options.length === 0 && <div className="text-[11px] text-slate-400 px-1 py-2">no matches</div>}
              {options.map((u) => (
                <button
                  key={u.user_id}
                  onClick={() => { onAdd(u.user_id); setOpen(false); setQ(''); }}
                  className="w-full flex items-center gap-2 text-left px-1.5 py-1 rounded hover:bg-pink-50 text-xs text-slate-700"
                >
                  <InitialsAvatar name={u.name} size={18} className="text-[9px]" />
                  <span className="truncate">{u.name}</span>
                </button>
              ))}
            </div>
          </div>
        </>,
        document.body,
      )}
    </span>
  );
};

// -----------------------------------------------------------------------------
// Category chip + picker (client / talent / personal / to film)
// -----------------------------------------------------------------------------
const CategoryChip = ({ category, clientId, clients, editable, onPick, onSetClientColor }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [anchor, setAnchor] = useState(null);
  const btnRef = useRef(null);
  const meta = categoryFor(category);
  const client = category === 'client' ? clients.find((c) => c.id === clientId) : null;
  const label = category === 'client'
    ? (client ? client.name : 'pick client…')
    : (meta ? meta.label : '+ file');
  const swatchColor = category === 'client'
    ? colorFor(clientColorFor(client)).tape
    : (meta ? colorFor(meta.color).tape : '#cbd5e1');

  const activeClients = useMemo(() => {
    const s = q.trim().toLowerCase();
    return clients.filter((c) => (c.active ?? true) !== false)
      .filter((c) => !s || (c.name || '').toLowerCase().includes(s));
  }, [clients, q]);

  const pick = (cat, clientIdVal = null) => {
    let color;
    if (cat === 'client') {
      const cli = clients.find((c) => c.id === clientIdVal);
      color = clientColorFor(cli);
    } else if (cat) {
      color = categoryFor(cat).color;
    }
    onPick({ category: cat, client_id: clientIdVal, color });
    setOpen(false); setQ('');
  };

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        onClick={(e) => {
          e.stopPropagation();
          if (!editable) return;
          if (!open && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setAnchor({ top: r.bottom + 4, left: r.left });
          }
          setOpen((o) => !o);
        }}
        disabled={!editable}
        className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] border ${category ? 'bg-white/70 text-slate-700 border-white/80' : 'bg-white/40 text-slate-500 border-dashed border-slate-300'} ${editable ? 'hover:brightness-95 cursor-pointer' : 'cursor-default'}`}
        title={category ? `filed under ${label}` : 'file under a category'}
        data-testid="todo-category-chip"
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: swatchColor }} />
        <span className="truncate max-w-[110px]">{label}</span>
        {category && editable && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); pick(null); }}
            className="ml-0.5 text-slate-400 hover:text-red-600"
          ><X className="w-2.5 h-2.5" /></span>
        )}
      </button>
      {open && anchor && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[9999] w-56 bg-white rounded-lg shadow-xl border border-slate-200 p-2"
            style={{ top: anchor.top, left: anchor.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[10px] uppercase tracking-widest text-slate-400 px-1 mb-1">file under</div>
            {CATEGORIES.filter((c) => c.id !== 'client').map((c) => (
              <button
                key={c.id}
                onClick={() => pick(c.id)}
                className={`w-full flex items-center gap-2 text-left px-1.5 py-1.5 rounded hover:bg-slate-50 text-xs text-slate-700 ${category === c.id ? 'bg-slate-50' : ''}`}
                data-testid={`todo-cat-${c.id}`}
              >
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: colorFor(c.color).tape }} />
                {c.label}
              </button>
            ))}
            <div className="mt-1 border-t border-slate-100 pt-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 px-1 mb-1">client</div>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="search clients…"
                className="h-7 w-full text-xs border border-slate-200 rounded px-2 outline-none focus:ring-2 focus:ring-pink-200 mb-1"
              />
              <div className="max-h-40 overflow-y-auto">
                {activeClients.length === 0 && <div className="text-[11px] text-slate-400 px-1 py-1.5">no clients match</div>}
                {activeClients.map((c) => (
                  <div key={c.id} className={`w-full flex items-center gap-2 px-1.5 py-1 rounded hover:bg-slate-50 text-xs text-slate-700 ${clientId === c.id ? 'bg-slate-50' : ''}`}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const cur = clientColorFor(c);
                        const idx = COLORS.findIndex((x) => x.id === cur);
                        const next = COLORS[(idx + 1) % COLORS.length].id;
                        onSetClientColor?.(c.id, next);
                      }}
                      title="click to change this client's colour"
                      className="w-3 h-3 rounded-full ring-1 ring-white/60 hover:scale-110 transition-transform shrink-0"
                      style={{ background: colorFor(clientColorFor(c)).tape }}
                      data-testid={`todo-client-swatch-${c.id}`}
                    />
                    <button
                      onClick={() => pick('client', c.id)}
                      className="text-left flex-1 truncate"
                      data-testid={`todo-client-${c.id}`}
                    >{c.name}</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};


// -----------------------------------------------------------------------------
// Sticky-note card
// -----------------------------------------------------------------------------
const StickyNoteCard = ({ todo, isMine, directory, clients, onToggleDone, onEditText, onAddTag, onRemoveTag, onCycleColor, onDelete, onEditDueDate, onSetCategory, onSetClientColor, onEditLink }) => {
  // Sticky colour is derived from the note's category so changing a client's colour
  // instantly updates every note filed under it (no batch backfill needed).
  const derivedColorId = (() => {
    if (todo.category === 'client') {
      const cli = clients.find((c) => c.id === todo.client_id);
      return clientColorFor(cli);
    }
    if (todo.category) return categoryFor(todo.category).color;
    return todo.color;
  })();
  const c = colorFor(derivedColorId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(todo.text);
  useEffect(() => { setDraft(todo.text); }, [todo.text]);

  const commit = async () => {
    setEditing(false);
    if (draft === todo.text) return;
    await onEditText(draft);
  };

  const tagUsers = (todo.tags || []).map((id) => directory.find((u) => u.user_id === id)).filter(Boolean);
  const ownerUser = directory.find((u) => u.user_id === todo.owner_id);

  return (
    <div
      className="relative rounded-md p-4 shadow-sm hover:shadow-md transition-shadow group"
      style={{ background: c.bg, border: `1px solid ${c.border}`, transform: 'rotate(-0.4deg)' }}
      data-testid={`todo-${todo.id}`}
    >
      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-14 h-4 opacity-70 rotate-2 rounded-sm" style={{ background: c.tape }} />

      <div className="flex items-start gap-2">
        <button
          onClick={onToggleDone}
          className={`shrink-0 w-5 h-5 mt-0.5 rounded border flex items-center justify-center transition-colors ${todo.done ? 'bg-slate-800 text-white border-slate-800' : 'bg-white border-slate-300 hover:border-slate-500'}`}
          title={todo.done ? 'restore from archive' : 'mark done'}
          data-testid={`todo-toggle-${todo.id}`}
        >
          {todo.done && <Check className="w-3 h-3" />}
        </button>

        <div className="flex-1 min-w-0">
          {editing && isMine ? (
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => { if (e.key === 'Escape') { setEditing(false); setDraft(todo.text); } }}
              rows={4}
              className="w-full bg-transparent text-sm outline-none resize-none placeholder:text-slate-400"
              style={{ color: c.ink }}
              placeholder="what needs doing?"
              data-testid={`todo-text-input-${todo.id}`}
            />
          ) : (
            <button
              type="button"
              onClick={() => isMine && setEditing(true)}
              disabled={!isMine}
              className={`text-left w-full text-sm whitespace-pre-wrap leading-snug ${todo.done ? 'line-through opacity-60' : ''} ${isMine ? 'cursor-text' : 'cursor-default'}`}
              style={{ color: c.ink }}
              data-testid={`todo-text-${todo.id}`}
            >
              {todo.text || <span className="italic opacity-60">empty note — click to write</span>}
            </button>
          )}
        </div>

        {(!isMine && ownerUser) || tagUsers.length > 0 || isMine ? (
          <div className="flex items-center gap-1 shrink-0">
            {!isMine && ownerUser && <TagChip user={ownerUser} small />}
            {tagUsers.map((u) => (
              <TagChip key={u.user_id} user={u} small onRemove={isMine ? () => onRemoveTag(u.user_id) : undefined} />
            ))}
            {isMine && (
              <TagPicker directory={directory} currentTags={todo.tags || []} onAdd={onAddTag} ownerLike={todo.owner_id} />
            )}
            {isMine && (
              <button
                onClick={onDelete}
                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600"
                data-testid={`todo-delete-${todo.id}`}
                title="delete note"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ) : null}
      </div>

      {/* Bottom row — due date, category and link, colour on the right */}
      <div className="mt-3 flex items-center flex-wrap gap-1.5">
        <DueDateChip iso={todo.due_date} editable={isMine} onChange={onEditDueDate} />
        <CategoryChip
          category={todo.category}
          clientId={todo.client_id}
          clients={clients}
          editable={isMine}
          onPick={onSetCategory}
          onSetClientColor={onSetClientColor}
        />
        <LinkChip url={todo.link} editable={isMine} onChange={onEditLink} />

        <div className="flex-1" />
        {isMine && (
          <button
            onClick={onCycleColor}
            title="change colour"
            className="w-4 h-4 rounded-full border border-white/60 shadow-sm"
            style={{ background: c.tape }}
            data-testid={`todo-color-${todo.id}`}
          />
        )}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// List-row card
// -----------------------------------------------------------------------------
const ListRow = ({ todo, isMine, directory, clients, onToggleDone, onEditText, onAddTag, onRemoveTag, onDelete, onEditDueDate, onCycleColor, onSetCategory, onSetClientColor, onEditLink }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(todo.text);
  useEffect(() => { setDraft(todo.text); }, [todo.text]);
  const commit = async () => { setEditing(false); if (draft !== todo.text) await onEditText(draft); };
  const tagUsers = (todo.tags || []).map((id) => directory.find((u) => u.user_id === id)).filter(Boolean);
  const ownerUser = directory.find((u) => u.user_id === todo.owner_id);
  const derivedColorId = (() => {
    if (todo.category === 'client') {
      const cli = clients.find((c) => c.id === todo.client_id);
      return clientColorFor(cli);
    }
    if (todo.category) return categoryFor(todo.category).color;
    return todo.color;
  })();
  const c = colorFor(derivedColorId);

  return (
    <div
      className="group relative flex items-start gap-3 py-2.5 pl-4 pr-3 rounded-md shadow-sm hover:shadow-md transition-shadow overflow-hidden"
      style={{ background: c.bg, border: `1px solid ${c.border}` }}
      data-testid={`todo-${todo.id}`}
    >
      {/* left tape strip */}
      <span className="absolute inset-y-0 left-0 w-1.5" style={{ background: c.tape }} />
      <button
        onClick={onToggleDone}
        className={`shrink-0 w-4 h-4 mt-1 rounded border flex items-center justify-center transition-colors ${todo.done ? 'bg-slate-800 text-white border-slate-800' : 'bg-white/80 border-white hover:border-slate-500'}`}
        title={todo.done ? 'restore' : 'mark done'}
        data-testid={`todo-toggle-${todo.id}`}
      >
        {todo.done && <Check className="w-3 h-3" />}
      </button>
      <div className="flex-1 min-w-0">
        {editing && isMine ? (
          <textarea
            autoFocus value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Escape') { setEditing(false); setDraft(todo.text); } }}
            rows={2}
            className="w-full text-sm outline-none resize-none bg-transparent placeholder:opacity-60"
            style={{ color: c.ink }}
          />
        ) : (
          <button
            type="button"
            onClick={() => isMine && setEditing(true)}
            disabled={!isMine}
            className={`text-left w-full text-sm whitespace-pre-wrap ${todo.done ? 'line-through opacity-60' : ''} ${isMine ? 'cursor-text' : 'cursor-default'}`}
            style={{ color: c.ink }}
          >
            {todo.text || <span className="italic opacity-60">empty note</span>}
          </button>
        )}
        <div className="mt-1 flex items-center flex-wrap gap-1.5">
          {todo.due_date && <DueDateChip iso={todo.due_date} editable={isMine} onChange={onEditDueDate} />}
          {isMine && !todo.due_date && (
            <DueDateChip iso={null} editable onChange={onEditDueDate} />
          )}
          <CategoryChip
            category={todo.category}
            clientId={todo.client_id}
            clients={clients}
            editable={isMine}
            onPick={onSetCategory}
            onSetClientColor={onSetClientColor}
          />
          <LinkChip url={todo.link} editable={isMine} onChange={onEditLink} />
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!isMine && ownerUser && <TagChip user={ownerUser} small />}
        {tagUsers.map((u) => (
          <TagChip key={u.user_id} user={u} small onRemove={isMine ? () => onRemoveTag(u.user_id) : undefined} />
        ))}
        {isMine && (
          <TagPicker directory={directory} currentTags={todo.tags || []} onAdd={onAddTag} ownerLike={todo.owner_id} />
        )}
      </div>
      {isMine && (
        <button
          onClick={onCycleColor}
          title="change colour"
          className="opacity-0 group-hover:opacity-100 shrink-0 w-4 h-4 rounded-full border border-white/70 shadow-sm mt-1"
          style={{ background: c.tape }}
          data-testid={`todo-color-${todo.id}`}
        />
      )}
      {isMine && (
        <button
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 shrink-0 text-slate-500 hover:text-red-600 mt-1"
          title="delete note"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Tabs strip — paste any url at the top of the board to pin it for quick access
// -----------------------------------------------------------------------------
const TabsBar = ({ tabs, onAdd, onPin, onDelete }) => {
  const [url, setUrl] = useState('');
  const [adding, setAdding] = useState(false);

  const submit = async () => {
    const v = url.trim();
    if (!v) return;
    setAdding(true);
    try { await onAdd(v); setUrl(''); }
    finally { setAdding(false); }
  };

  const sorted = [...tabs].sort((a, b) => (Number(b.pinned) - Number(a.pinned)) || (new Date(b.created_at) - new Date(a.created_at)));

  return (
    <div className="mb-5 bg-white border border-slate-200 rounded-2xl p-3" data-testid="todo-tabs-bar">
      <div className="flex items-center gap-2">
        <LinkIcon className="w-4 h-4 text-pink-400 shrink-0" />
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="paste a url to pin it up top…"
          className="flex-1 h-8 text-xs border border-slate-200 rounded-md px-2 outline-none focus:ring-2 focus:ring-pink-200"
          data-testid="todo-tab-input"
        />
        <button
          onClick={submit}
          disabled={adding || !url.trim()}
          className="h-8 px-3 rounded-md bg-pink-500 hover:bg-pink-600 text-white text-xs font-medium disabled:opacity-50"
          data-testid="todo-tab-add"
        >
          {adding ? '…' : 'add'}
        </button>
      </div>
      {sorted.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {sorted.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1 h-7 px-2 rounded-full text-[11px] bg-pink-50 border border-pink-200" data-testid={`todo-tab-${t.id}`}>
              <button
                onClick={() => onPin(t)}
                title={t.pinned ? 'unpin' : 'pin'}
                className={t.pinned ? 'text-pink-600' : 'text-slate-400 hover:text-pink-600'}
                data-testid={`todo-tab-pin-${t.id}`}
              >
                <Pin className="w-3 h-3" fill={t.pinned ? 'currentColor' : 'none'} />
              </button>
              <a href={t.url} target="_blank" rel="noreferrer" className="text-slate-700 hover:text-pink-700 max-w-[160px] truncate" data-testid={`todo-tab-link-${t.id}`}>
                {t.title || t.url}
              </a>
              <button onClick={() => onDelete(t.id)} className="text-slate-400 hover:text-red-600" data-testid={`todo-tab-delete-${t.id}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------
const Todos = () => {
  const { user } = useAuth();
  const [todos, setTodos] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [clients, setClients] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem('todos.view') || 'stickies'; } catch { return 'stickies'; }
  });
  const [showArchive, setShowArchive] = useState(false);

  useEffect(() => { try { localStorage.setItem('todos.view', view); } catch {} }, [view]);

  const load = async () => {
    try {
      const [{ data: t }, { data: dir }, { data: cli }, { data: tb }] = await Promise.all([
        api.get('/todos'),
        api.get('/team/directory'),
        api.get('/clients'),
        api.get('/todos/tabs'),
      ]);
      setTodos(t || []);
      setDirectory(dir || []);
      setClients(cli || []);
      setTabs(tb || []);
    } catch { toast.error('failed to load todos'); }
    finally { setLoaded(true); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const addTab = async (url) => {
    try {
      const { data } = await api.post('/todos/tabs', { url });
      setTabs((ts) => [data, ...ts]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'failed to add link'); }
  };

  const pinTab = async (t) => {
    try {
      const { data } = await api.patch(`/todos/tabs/${t.id}`, { pinned: !t.pinned });
      setTabs((ts) => ts.map((x) => (x.id === t.id ? data : x)));
    } catch { toast.error('failed to update'); }
  };

  const deleteTab = async (id) => {
    try {
      await api.delete(`/todos/tabs/${id}`);
      setTabs((ts) => ts.filter((x) => x.id !== id));
    } catch { toast.error('failed to delete'); }
  };

  const addTodo = async () => {
    try {
      const { data } = await api.post('/todos', { text: '', color: COLORS[Math.floor(Math.random() * COLORS.length)].id });
      setTodos((ts) => [data, ...ts]);
    } catch { toast.error('failed to add todo'); }
  };

  const patch = async (id, body) => {
    try {
      const { data } = await api.patch(`/todos/${id}`, body);
      setTodos((ts) => ts.map((t) => t.id === id ? data : t));
    } catch { toast.error('save failed'); }
  };

  const toggleDone = (t) => patch(t.id, { done: !t.done });

  const removeTodo = async (id) => {
    if (!window.confirm('delete this note?')) return;
    try {
      await api.delete(`/todos/${id}`);
      setTodos((ts) => ts.filter((t) => t.id !== id));
    } catch (e) { toast.error(e?.response?.data?.detail || 'delete failed'); }
  };

  const activeTodos = todos.filter((t) => !t.done);
  const doneTodos = todos.filter((t) => t.done);
  const mine = activeTodos.filter((t) => t.owner_id === user?.user_id);
  const tagged = activeTodos.filter((t) => t.owner_id !== user?.user_id);

  // List view grouping — sort by due date (undated last, overdue first)
  const listGroups = useMemo(() => {
    const groups = new Map();
    const push = (key, t) => {
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(t);
    };
    activeTodos.forEach((t) => push(t.due_date || '__none__', t));
    const entries = Array.from(groups.entries());
    entries.sort((a, b) => {
      if (a[0] === '__none__') return 1;
      if (b[0] === '__none__') return -1;
      return a[0].localeCompare(b[0]);
    });
    return entries;
  }, [activeTodos]);

  const bindProps = (t) => ({
    todo: t,
    isMine: t.owner_id === user?.user_id,
    directory,
    clients,
    onToggleDone: () => toggleDone(t),
    onEditText: (text) => patch(t.id, { text }),
    onAddTag: (uid) => patch(t.id, { tags: [...(t.tags || []), uid] }),
    onRemoveTag: (uid) => patch(t.id, { tags: (t.tags || []).filter((x) => x !== uid) }),
    onEditDueDate: (iso) => patch(t.id, { due_date: iso || '' }),
    onEditLink: (url) => patch(t.id, { link: url || '' }),
    onSetCategory: ({ category, client_id, color }) => {
      const body = { category: category || '', client_id: client_id || null };
      if (color) body.color = color;
      patch(t.id, body);
    },
    onSetClientColor: async (client_id, color) => {
      try {
        const { data } = await api.patch(`/clients/${client_id}`, { color });
        setClients((cs) => cs.map((c) => c.id === client_id ? data : c));
        // recolour any open notes filed under this client so the UI reflects instantly
        setTodos((ts) => ts.map((n) => n.client_id === client_id && n.category === 'client' ? { ...n, color } : n));
        // persist the new colour to every note filed under this client
        const affected = todos.filter((n) => n.client_id === client_id && n.category === 'client');
        await Promise.all(affected.map((n) => api.patch(`/todos/${n.id}`, { color })));
      } catch { toast.error('failed to change client colour'); }
    },
    onCycleColor: () => {
      const idx = COLORS.findIndex((c) => c.id === t.color);
      const next = COLORS[(idx + 1) % COLORS.length];
      patch(t.id, { color: next.id });
    },
    onDelete: () => removeTodo(t.id),
  });

  const remaining = activeTodos.length;

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <StickyNote className="w-6 h-6 text-pink-500" />
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">to‑do</h1>
            <p className="text-xs text-slate-500">private board · tag a team‑mate to share a note · ticked notes move to the archive</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="inline-flex items-center rounded-md border border-slate-200 bg-white overflow-hidden" data-testid="todos-view-toggle">
            <button
              onClick={() => setView('stickies')}
              className={`inline-flex items-center gap-1.5 px-3 h-10 text-sm ${view === 'stickies' ? 'bg-pink-50 text-pink-700' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid="todos-view-stickies"
              title="sticky board"
            >
              <LayoutGrid className="w-4 h-4" /> stickies
            </button>
            <button
              onClick={() => setView('list')}
              className={`inline-flex items-center gap-1.5 px-3 h-10 text-sm border-l border-slate-200 ${view === 'list' ? 'bg-pink-50 text-pink-700' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid="todos-view-list"
              title="prioritised list by date"
            >
              <ListIcon className="w-4 h-4" /> list
            </button>
          </div>

          {/* Archive folder */}
          <button
            onClick={() => setShowArchive((s) => !s)}
            className={`inline-flex items-center gap-1.5 px-3 h-10 rounded-md border text-sm ${showArchive ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
            data-testid="todos-archive-btn"
            title="completed notes"
          >
            <Archive className="w-4 h-4" /> complete
            <span className={`text-[11px] rounded-full px-1.5 py-0.5 ${showArchive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>{doneTodos.length}</span>
          </button>

          <div className="text-xs text-slate-500 ml-1">{remaining} open</div>
          <button
            onClick={addTodo}
            className="inline-flex items-center gap-1.5 px-3 h-10 rounded-md bg-pink-500 text-white hover:bg-pink-600 text-sm font-medium"
            data-testid="todo-add"
          >
            <Plus className="w-4 h-4" /> new note
          </button>
        </div>
      </div>

      <TabsBar tabs={tabs} onAdd={addTab} onPin={pinTab} onDelete={deleteTab} />

      {/* Archive drawer */}
      {showArchive && (
        <div className="mb-6 bg-slate-900 text-slate-100 rounded-2xl p-5" data-testid="todos-archive-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Archive className="w-4 h-4" /> complete · archive
              <span className="text-xs text-slate-400">{doneTodos.length} note{doneTodos.length === 1 ? '' : 's'}</span>
            </div>
            <button onClick={() => setShowArchive(false)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          {doneTodos.length === 0 ? (
            <div className="text-slate-400 text-sm py-6 text-center">nothing here yet — completed notes will land here.</div>
          ) : (
            <div className="space-y-2">
              {doneTodos.map((t) => {
                const owner = directory.find((u) => u.user_id === t.owner_id);
                const isMine = t.owner_id === user?.user_id;
                return (
                  <div key={t.id} className="flex items-center gap-3 py-2 px-3 bg-slate-800/60 rounded-md" data-testid={`archive-todo-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm line-through text-slate-300 truncate">{t.text || <span className="italic text-slate-500">empty note</span>}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        {t.done_at && <span>completed {new Date(t.done_at).toLocaleDateString()}</span>}
                        {!isMine && owner && <span>· by {owner.name}</span>}
                        {t.due_date && <span>· due {fmtDueLongHeading(t.due_date)}</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => toggleDone(t)}
                      className="inline-flex items-center gap-1 text-[11px] text-slate-300 hover:text-white px-2 py-1 rounded hover:bg-slate-700"
                      title="restore"
                      data-testid={`archive-restore-${t.id}`}
                    >
                      <ArchiveRestore className="w-3.5 h-3.5" /> restore
                    </button>
                    {isMine && (
                      <button
                        onClick={() => removeTodo(t.id)}
                        className="text-slate-400 hover:text-red-400 p-1 rounded"
                        title="delete forever"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {loaded && activeTodos.length === 0 && !showArchive && (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-12 text-center text-sm text-slate-400">
          nothing on the board yet — tap <span className="font-medium text-pink-600">new note</span> to jot something down
        </div>
      )}

      {/* Sticky view */}
      {view === 'stickies' && (
        <>
          {mine.length > 0 && (
            <div className="mb-8">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-2">my notes</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {mine.map((t) => <StickyNoteCard key={t.id} {...bindProps(t)} />)}
              </div>
            </div>
          )}
          {tagged.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-2">shared with me</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {tagged.map((t) => <StickyNoteCard key={t.id} {...bindProps(t)} />)}
              </div>
            </div>
          )}
        </>
      )}

      {/* List view — grouped and headed by date */}
      {view === 'list' && (
        <div className="space-y-6">
          {listGroups.map(([dateKey, items]) => (
            <div key={dateKey} data-testid={`todos-list-group-${dateKey}`}>
              <div className="flex items-baseline gap-2 mb-2">
                <div className={`text-sm font-semibold ${dateKey === '__none__' ? 'text-slate-400' : (daysUntil(dateKey) < 0 ? 'text-red-600' : (daysUntil(dateKey) === 0 ? 'text-amber-600' : 'text-slate-800'))}`}>
                  {fmtDueLongHeading(dateKey === '__none__' ? null : dateKey)}
                </div>
                <div className="text-[11px] text-slate-400">{items.length} note{items.length === 1 ? '' : 's'}</div>
              </div>
              <div className="space-y-2">
                {items.map((t) => <ListRow key={t.id} {...bindProps(t)} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Todos;
